// src/components/TaxDashboard.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import FiscalYearSwitcher from './FiscalYearSwitcher';
import vatAPI from '../api/vatAPI';
import percentageTaxAPI from '../api/percentageTaxAPI';
import accountingClientsAPI from '../api/accountingClientsAPI';
import formAPI from '../api/formAPI';
import { computeVatPayable, computePercentagePayable, formatPHP } from '../utils/taxCompute';
import VATQuarterBarChart from './VATQuarterBarChart';
import PercentageTaxQuarterBarChart from './PercentageTaxQuarterBarChart';
import useCompanyContext from '../hooks/useCompanyContext';
import {
  DocumentTextIcon,
  ChartBarIcon,
  BriefcaseIcon,
  ArrowTrendingUpIcon,
  LightBulbIcon,
  PhoneIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const currentQuarter = () => {
  const m = new Date().getMonth() + 1;
  if (m <= 3) return 1;
  if (m <= 6) return 2;
  if (m <= 9) return 3;
  return 4;
};

const TaxDashboard = () => {
  const navigate = useNavigate();
  
  // ✅ NEW: Use hook instead of useMemo with empty deps
  // This will update when company switches in header
  const { accountingClientId: contextAccountingClientId } = useCompanyContext();

  // Reactive: updates when contextAccountingClientId changes (via company switch)
  const accountingClientId = contextAccountingClientId || '';

  const fiscalYear = useMemo(() => {
    if (!accountingClientId) return null;
    const saved = parseInt(localStorage.getItem(`activeFiscalYear:${accountingClientId}`), 10);
    return !isNaN(saved) ? saved : new Date().getFullYear();
  }, [accountingClientId]);

  // Period selection
  const [selectedQuarter, setSelectedQuarter] = useState(() => currentQuarter());

  // Client state
  const [client, setClient]             = useState(null);
  const [clientLoading, setClientLoading] = useState(true);

  // Tax summary state
  const [vatSummary, setVatSummary]                       = useState(null);
  const [percentageTaxSummary, setPercentageTaxSummary]   = useState(null);
  const [loading, setLoading]                             = useState(false);
  const [error, setError]                                 = useState(null);

  // Obligation flags derived from client (stable once client loads)
  const hasVAT           = !!client?.taxObligations?.vat;
  const hasPercentageTax = !!client?.taxObligations?.percentageTax;

  // Phase 1: fetch client once
  useEffect(() => {
    if (!accountingClientId) return;
    let cancelled = false;

    const run = async () => {
      try {
        setClientLoading(true);
        const res = await accountingClientsAPI.getById(accountingClientId);
        if (!cancelled) setClient(res.data?.client ?? null);
      } catch {
        if (!cancelled) setClient(null);
      } finally {
        if (!cancelled) setClientLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [accountingClientId]);

  // Phase 2: fetch tax summaries whenever client, period, or fiscal year changes
  useEffect(() => {
    if (!accountingClientId || !fiscalYear || !client) return;
    if (!hasVAT && !hasPercentageTax) return;

    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = { quarter: selectedQuarter, fiscalYear };

        // VAT: fetch individual records + form2306 then compute in-memory
        // (mirrors VATManagement.calculateSummary — avoids aggregate ObjectId cast issue)
        const [vatRecordsRes, form2306Res, pctRecordsRes] = await Promise.all([
          hasVAT
            ? vatAPI.getByClient(accountingClientId, params).catch(() => null)
            : Promise.resolve(null),
          hasVAT || hasPercentageTax
            ? formAPI.getForm2306ByClient(accountingClientId, { ...params, limit: 5000 }).catch(() => null)
            : Promise.resolve(null),
          hasPercentageTax
            ? percentageTaxAPI.getByClient(accountingClientId, params).catch(() => null)
            : Promise.resolve(null),
        ]);

        if (cancelled) return;

        // Shared form2306 records for this quarter
        const form2306Records = Array.isArray(form2306Res?.data?.records)
          ? form2306Res.data.records
          : Array.isArray(form2306Res?.data)
          ? form2306Res.data
          : [];
        const total2306 = form2306Records.reduce((s, r) => s + Number(r.taxWithheld || 0), 0);

        // Build vatSummary shape from fetched records
        if (hasVAT) {
          const vatRecords = Array.isArray(vatRecordsRes?.data)
            ? vatRecordsRes.data
            : Array.isArray(vatRecordsRes?.data?.records)
            ? vatRecordsRes.data.records
            : [];
          const outputVAT = vatRecords
            .filter((r) => r.recordType === 'output')
            .reduce((s, r) => s + Number(r.vatAmount || 0), 0);
          const inputVAT = vatRecords
            .filter((r) => r.recordType === 'input')
            .reduce((s, r) => s + Number(r.vatAmount || 0), 0);

          setVatSummary(vatRecords.length > 0 ? {
            outputVAT: { totalVAT: outputVAT, count: vatRecords.filter(r => r.recordType === 'output').length },
            inputVAT:  { totalVAT: inputVAT,  count: vatRecords.filter(r => r.recordType === 'input').length },
            form2306:  { totalTaxWithheld: total2306 },
            excessFromPrevQuarter: 0,
          } : null);
        }

        // Build percentageTaxSummary from fetched records
        // (mirrors PercentageTaxFormManagement.periodSummary)
        if (hasPercentageTax) {
          const pctRecords = Array.isArray(pctRecordsRes?.data?.records)
            ? pctRecordsRes.data.records
            : Array.isArray(pctRecordsRes?.data)
            ? pctRecordsRes.data
            : [];
          const salesTaxDue = pctRecords
            .filter((r) => r.category === 'sales')
            .reduce((s, r) => s + Number(r.taxDue || 0), 0);
          const incomeTaxDue = pctRecords
            .filter((r) => r.category === 'income')
            .reduce((s, r) => s + Number(r.taxDue || 0), 0);
          const netTaxPayable = salesTaxDue - incomeTaxDue - total2306;

          setPercentageTaxSummary(pctRecords.length > 0 ? {
            salesTaxDue,
            incomeTaxDue,
            total2306,
            excessFromPrevQuarter: 0,
            netTaxPayable,
          } : null);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load tax data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, selectedQuarter, fiscalYear]);

  // Computed values (pure, memoized)
  const vatComputed = useMemo(
    () => (hasVAT && vatSummary ? computeVatPayable(vatSummary) : null),
    [hasVAT, vatSummary]
  );

  const percentageComputed = useMemo(
    () => (hasPercentageTax && percentageTaxSummary ? computePercentagePayable(percentageTaxSummary) : null),
    [hasPercentageTax, percentageTaxSummary]
  );

  const noModules = !clientLoading && !loading && !hasVAT && !hasPercentageTax;

  return (
    <Layout pageTitle="Tax Dashboard">
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-8">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600 mt-2">FY {fiscalYear}</p>
            </div>
            <FiscalYearSwitcher accountingClientId={accountingClientId} />
          </div>

          {/* Info Banner */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
            <p className="text-teal-900">
              Manage and track all tax obligations for your accounting client in one place.
            </p>
          </div>

          {/* Bar Charts — shown first */}
          {!clientLoading && hasVAT && (
            <VATQuarterBarChart clientId={accountingClientId} fiscalYear={fiscalYear} />
          )}
          {!clientLoading && hasPercentageTax && (
            <PercentageTaxQuarterBarChart clientId={accountingClientId} fiscalYear={fiscalYear} />
          )}

          {/* Quarter Selector */}
          {(hasVAT || hasPercentageTax) && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Select Quarter</h3>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map((q) => (
                    <button
                      key={q}
                      onClick={() => setSelectedQuarter(q)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedQuarter === q
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Q{q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Status banners */}
          {noModules && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
              <p className="text-yellow-900 font-medium">No tax modules enabled for this client</p>
              <p className="text-yellow-700 text-sm mt-2">
                Enable VAT or Percentage Tax in the client tax obligations settings.
              </p>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-900 font-medium">Error loading tax data</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Obligation Cards — combined with quarterly summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* VAT — combined card */}
            {hasVAT ? (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow hover:border-teal-300">
                <DocumentTextIcon className="h-10 w-10 mb-3 text-teal-600" />
                <h3 className="font-semibold mb-1 text-gray-900">Value-Added Tax (VAT)</h3>
                <p className="text-sm text-teal-700 font-medium mb-1">BIR Form 2550Q</p>
                <p className="text-sm text-gray-700 mb-4">Quarterly VAT filing and records</p>

                <hr className="border-teal-200 mb-4" />

                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Q{selectedQuarter} {fiscalYear} Summary
                </p>
                {loading ? (
                  <p className="text-gray-400 text-sm animate-pulse">Loading...</p>
                ) : vatComputed ? (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Output VAT</span>
                      <span className="font-medium">{formatPHP(vatComputed.outputVat)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Less: Input VAT</span>
                      <span className="font-medium">{formatPHP(vatComputed.inputVat)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Less: Form 2306</span>
                      <span className="font-medium">{formatPHP(vatComputed.tax2306)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Less: Excess from Prev. Quarter</span>
                      <span className="font-medium">{formatPHP(vatComputed.excess)}</span>
                    </div>
                    <hr className="border-teal-200" />
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">VAT Payable</span>
                      <span className={`text-xl font-bold ${vatComputed.payable < 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatPHP(vatComputed.payable)}
                      </span>
                    </div>
                    {vatComputed.carryOver > 0 && (
                      <p className="text-sm text-green-700">
                        Carry-over to next quarter: {formatPHP(vatComputed.carryOver)}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No VAT records for Q{selectedQuarter} {fiscalYear}</p>
                )}
              </div>
            ) : (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow hover:border-teal-300">
                <DocumentTextIcon className="h-10 w-10 mb-3 text-teal-600" />
                <h3 className="font-semibold mb-1 text-gray-900">Value-Added Tax (VAT)</h3>
                <p className="text-sm text-teal-700 font-medium mb-2">BIR Form 2550Q</p>
                <p className="text-sm text-gray-700">Quarterly VAT filing and records</p>
              </div>
            )}

            {/* Percentage Tax — combined card */}
            {hasPercentageTax ? (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow hover:border-teal-300">
                <ChartBarIcon className="h-10 w-10 mb-3 text-teal-600" />
                <h3 className="font-semibold mb-1 text-gray-900">Percentage Tax</h3>
                <p className="text-sm text-teal-700 font-medium mb-1">BIR Form 2551Q</p>
                <p className="text-sm text-gray-700 mb-4">Quarterly percentage tax filing</p>

                <hr className="border-teal-200 mb-4" />

                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Q{selectedQuarter} {fiscalYear} Summary
                </p>
                {loading ? (
                  <p className="text-gray-400 text-sm animate-pulse">Loading...</p>
                ) : percentageTaxSummary ? (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Sales Tax Due</span>
                      <span className="font-medium">{formatPHP(percentageTaxSummary.salesTaxDue || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Income Tax Due</span>
                      <span className="font-medium">{formatPHP(percentageTaxSummary.incomeTaxDue || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Less: Form 2306</span>
                      <span className="font-medium">{formatPHP(percentageTaxSummary.total2306 || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Less: Excess from Prev. Quarter</span>
                      <span className="font-medium">{formatPHP(percentageTaxSummary.excessFromPrevQuarter || 0)}</span>
                    </div>
                    <hr className="border-teal-200" />
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">Tax Payable</span>
                      <span className={`text-xl font-bold ${percentageComputed < 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatPHP(percentageComputed)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No Percentage Tax records for Q{selectedQuarter} {fiscalYear}</p>
                )}
              </div>
            ) : (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow hover:border-teal-300">
                <ChartBarIcon className="h-10 w-10 mb-3 text-teal-600" />
                <h3 className="font-semibold mb-1 text-gray-900">Percentage Tax</h3>
                <p className="text-sm text-teal-700 font-medium mb-2">BIR Form 2551Q</p>
                <p className="text-sm text-gray-700">Quarterly percentage tax filing</p>
              </div>
            )}

            {/* Withholding Tax — static */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow hover:border-teal-300">
              <BriefcaseIcon className="h-10 w-10 mb-3 text-teal-600" />
              <h3 className="font-semibold mb-1 text-gray-900">Withholding Tax</h3>
              <p className="text-sm text-teal-700 font-medium mb-2">BIR Form 2316</p>
              <p className="text-sm text-gray-700">Employee compensation withholding</p>
            </div>

            {/* Income Tax — static */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow hover:border-teal-300">
              <ArrowTrendingUpIcon className="h-10 w-10 mb-3 text-teal-600" />
              <h3 className="font-semibold mb-1 text-gray-900">Income Tax</h3>
              <p className="text-sm text-teal-700 font-medium mb-2">BIR Forms 1701/1702</p>
              <p className="text-sm text-gray-700">Annual income tax filing</p>
            </div>

          </div>

          {/* View details action */}
          {!clientLoading && (hasVAT || hasPercentageTax) && (
            <div className="flex">
              <button
                onClick={() => navigate('/client/tax-obligation')}
                className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition font-medium inline-flex items-center gap-2"
              >
                View Tax Obligations
                <ArrowRightIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Quick links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
                <LightBulbIcon className="h-5 w-5" />
                Quick Tips
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>Update tax records monthly</li>
                <li>Track filing deadlines</li>
                <li>Keep records organized by type</li>
                <li>Review quarterly summaries</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
                <PhoneIcon className="h-5 w-5" />
                Need Help?
              </h3>
              <p className="text-sm text-gray-700 mb-3">
                For more information about tax compliance, visit the Tax Obligations page or contact your tax administrator.
              </p>
              <button
                onClick={() => navigate('/client/tax-obligation')}
                className="text-teal-600 hover:text-teal-700 text-sm font-semibold"
              >
                View Tax Obligations
              </button>
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default TaxDashboard;
